// My Dashboard · "Trade Account sign-up" — my customers with no logged-in
// orders (can't see trade/tier discounts), biggest spend first.
import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Store, Send, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { TradeAccountBadge, setTradeStatus } from "../../components/crm/TradeAccount";

const gbp = (v) => `£${Number(v || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
const chip = (on) => `px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${on ? "bg-orange-500/15 border-orange-400/50 text-orange-200" : "border-white/10 text-slate-400 hover:text-white"}`;

export default function TradeSignupsBar({ am, onOverlay, onCount }) {
  const [open, setOpen] = useState(true);
  const [gold, setGold] = useState(false);
  const [minF, setMinF] = useState(true);
  const [data, setData] = useState({ items: [], total: 0, total_value: 0 });
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    const p = new URLSearchParams({ limit: "50" });
    if (am) p.set("am", am);
    if (gold) p.set("gold_plus", "true");
    if (minF) p.set("min_filter", "true");
    try { const { data: d } = await api.get(`/me/trade-signups?${p}`); setData(d); onCount?.(d.total ?? (d.items || []).length); }
    catch { setData({ items: [], total: 0, total_value: 0 }); }
  }, [am, gold, minF]);
  useEffect(() => { load(); }, [load]);

  const mark = async (r, status) => {
    setBusy((b) => ({ ...b, [r.id]: true }));
    try { await setTradeStatus(r.id, status); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy((b) => ({ ...b, [r.id]: false })); }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="bar-trade-signups">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="bar-trade-signups-toggle"
        className="dash-sec-head w-full flex items-center gap-3 px-4 py-3 text-left transition-colors">
        {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
        <Store className="w-4 h-4 shrink-0 text-rose-300" />
        <span className="text-sm font-semibold text-slate-100">Trade Account sign-up</span>
        <span className="text-[11px] font-mono text-slate-400 tabular-nums" data-testid="bar-trade-signups-count">{data.total}</span>
        <span className="ml-auto text-[11.5px] text-slate-400 truncate">{gbp(data.total_value)} spent without an account · can't see trade discounts</span>
      </button>
      {open && (
        <div className="border-t border-white/[0.06]">
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-white/[0.04]">
            <button type="button" className={chip(minF)} onClick={() => setMinF((v) => !v)} data-testid="trade-filter-min">2+ orders or £1k+</button>
            <button type="button" className={chip(!minF)} onClick={() => setMinF(false)} data-testid="trade-filter-all">Show all</button>
            <button type="button" className={chip(gold)} onClick={() => setGold((v) => !v)} data-testid="trade-filter-gold">Gold tier and above</button>
          </div>
          <div className="overflow-x-auto">
            {data.items.length === 0 ? (
              <div className="px-4 py-6 text-center text-[12px] text-slate-500" data-testid="trade-signups-empty">Everyone in this view has a trade account.</div>
            ) : (
              <table className="w-full text-[12px]">
                <thead><tr className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-left">
                  <th className="px-4 py-2">Customer</th><th className="px-2">Tier</th><th className="px-2 text-right">Orders</th>
                  <th className="px-2 text-right">Guest</th><th className="px-2 text-right">Spend</th><th className="px-2">Status</th><th className="px-4 text-right">Action</th>
                </tr></thead>
                <tbody>
                  {data.items.map((r) => {
                    const ta = r.trade_account;
                    return (
                      <tr key={r.id} className="border-t border-white/[0.04] hover:bg-white/[0.02]" data-testid={`trade-row-${r.id}`}>
                        <td className="px-4 py-2 min-w-[180px]">
                          <button type="button" onClick={() => onOverlay?.(r.id)} className="text-slate-100 font-medium hover:text-orange-300 text-left" data-testid={`trade-open-${r.id}`}>{r.full_name || r.email}</button>
                          <div className="text-[10.5px] text-slate-500 truncate">{r.company || r.email}</div>
                        </td>
                        <td className="px-2 text-slate-300">{r.vip_tier || "—"}</td>
                        <td className="px-2 text-right tabular-nums text-slate-200">{ta.total_orders}</td>
                        <td className="px-2 text-right tabular-nums text-slate-400">{ta.guest_orders}</td>
                        <td className="px-2 text-right tabular-nums text-emerald-300 font-semibold">{gbp(ta.total_value)}</td>
                        <td className="px-2"><TradeAccountBadge ta={ta} testid={`trade-status-${r.id}`} /></td>
                        <td className="px-4 text-right whitespace-nowrap">
                          {ta.status !== "invite_sent" && (
                            <button type="button" disabled={busy[r.id]} onClick={() => mark(r, "invite_sent")} data-testid={`trade-invite-${r.id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 disabled:opacity-50">
                              <Send className="w-3 h-3" /> Mark invite sent
                            </button>
                          )}
                          {ta.status !== "created_by_us" && (
                            <button type="button" disabled={busy[r.id]} onClick={() => mark(r, "created_by_us")} data-testid={`trade-created-${r.id}`} title="We created the account for them"
                              className="ml-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border border-sky-500/40 text-sky-200 hover:bg-sky-500/10 disabled:opacity-50">
                              <UserPlus className="w-3 h-3" /> Created
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {data.total > data.items.length && <div className="px-4 py-2 border-t border-white/[0.04] text-[11px] text-slate-400">Showing top {data.items.length} of {data.total} by spend</div>}
        </div>
      )}
    </div>
  );
}
